import {
  CircleDot,
  Goal,
  MicVocal,
  Trophy,
  Volleyball,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { BaseballTrendPresetId } from "@shared/schemas/api";

interface BaseballTrendPresetTile {
  id: BaseballTrendPresetId;
  nameKey: string;
  previewImage: string;
}

interface BaseballTrendPresetGridProps {
  presets: BaseballTrendPresetTile[];
  selectedId: BaseballTrendPresetId | null;
  onSelect: (id: BaseballTrendPresetId) => void;
}

const PRESET_ICON: Record<BaseballTrendPresetId, LucideIcon> = {
  baseball: CircleDot,
  soccer: Goal,
  basketball: Volleyball,
  concert: MicVocal,
};

export function BaseballTrendPresetGrid({
  presets,
  selectedId,
  onSelect,
}: BaseballTrendPresetGridProps) {
  const { t } = useLanguage();

  const presetName = (nameKey: string): string => {
    const map = t.baseballTrend.presets as Record<string, string>;
    return map[nameKey] ?? nameKey;
  };

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.baseballTrend.presetTitle}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t.baseballTrend.presetHint}
        </p>
      </div>
      <div className="flex md:flex-wrap gap-2 md:gap-3 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {presets.map((preset) => {
          const isActive = selectedId === preset.id;
          const Icon = PRESET_ICON[preset.id] ?? Trophy;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onSelect(preset.id)}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 px-4 py-3 w-[100px] md:w-[110px] flex-shrink-0 snap-start text-xs md:text-sm font-medium transition-all duration-150",
                isActive
                  ? "border-brand bg-brand/10 text-brand shadow-sm"
                  : "border-border bg-card text-foreground hover:border-brand/40 hover:bg-muted",
              )}
            >
              <Icon
                className={cn(
                  "w-7 h-7 md:w-8 md:h-8 shrink-0",
                  isActive ? "text-brand" : "text-muted-foreground",
                )}
                strokeWidth={1.75}
              />
              <span className="leading-tight text-center">
                {presetName(preset.nameKey)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
